import { FC } from 'react';
import { cn } from '../../../utils/cn';
import type { TherapistStatus } from '../../../api/adminTherapistApi';

interface TherapistStatusBadgeProps {
  status: TherapistStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<
  TherapistStatus,
  { label: string; bgColor: string; textColor: string; dotColor: string }
> = {
  PENDING: {
    label: 'Pending',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-800 dark:text-yellow-300',
    dotColor: 'bg-yellow-500',
  },
  ACTIVE: {
    label: 'Active',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-800 dark:text-green-300',
    dotColor: 'bg-green-500',
  },
  SUSPENDED: {
    label: 'Suspended',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-800 dark:text-orange-300',
    dotColor: 'bg-orange-500',
  },
  REJECTED: {
    label: 'Rejected',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-800 dark:text-red-300',
    dotColor: 'bg-red-500',
  },
  INACTIVE: {
    label: 'Inactive',
    bgColor: 'bg-gray-100 dark:bg-slate-700',
    textColor: 'text-gray-800 dark:text-slate-300',
    dotColor: 'bg-gray-500',
  },
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

const dotSizes = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
};

export const TherapistStatusBadge: FC<TherapistStatusBadgeProps> = ({
  status,
  size = 'md',
  className,
}) => {
  const config = statusConfig[status] || statusConfig.INACTIVE;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        config.bgColor,
        config.textColor,
        sizeStyles[size],
        className
      )}
    >
      <span className={cn('rounded-full', config.dotColor, dotSizes[size])} />
      {config.label}
    </span>
  );
};

export default TherapistStatusBadge;
